import { useState } from 'react';

import {
  ListChecks,
  MailPlus,
  Plus,
  StickyNote,
  UserRoundPlus,
} from 'lucide-react';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router';

import useCrm from '@/features/crm/hooks/useCrm';
import paths from '@/lib/paths';

import { useEmail } from '../providers/EmailProvider/EmailProvider';
import { Button } from '../shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';

type GlobalActionType =
  | 'ADD_NOTE'
  | 'CREATE_TASK'
  | 'EXPORT_EMAIL'
  | 'EXPORT_PARTY';

export default function CreateButton() {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const {
    canCreateTask,
    createTask,
    canExportEmail,
    exportEmail,
    canExportPartyToMissingCrms,
    exportPartyToMissingCrms,
  } = useCrm();

  const { party, email } = useEmail();

  const startAction = (actionType: GlobalActionType, action?: VoidFunction) => {
    posthog.capture('Global action clicked', {
      actionType,
      partyId: party.party?.id,
      messageId: email?.messageId,
    });

    setIsOpen(false);
    action?.();
  };

  if (party?.status !== 'Selected') return null;

  return (
    <>
      {isOpen && (
        <div className="fixed top-0 right-0 left-0 z-50 h-96 bg-linear-to-b from-black/30 to-transparent" />
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger className="relative">
          <Button size="xs" asChild>
            <span data-onboarding-id="create-actions">
              <Plus /> {t('GLOBAL_CREATE_BUTTON')}
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex flex-col items-end gap-2 border-none bg-transparent p-2 shadow-none"
          collisionPadding={0}
          sideOffset={0}
          align="center"
        >
          <Button variant="default" size="sm" asChild className="shadow-xl">
            <Link
              to={generatePath(paths.notes.create)}
              onClick={() => startAction('ADD_NOTE')}
            >
              <StickyNote /> {t('QUICK_ACTION_ADD_NOTE')}
            </Link>
          </Button>

          {canCreateTask && (
            <Button
              variant="default"
              size="sm"
              className="shadow-xl"
              onClick={() => startAction('CREATE_TASK', createTask)}
            >
              <ListChecks />

              {t('QUICK_ACTION_CREATE_TASK')}
            </Button>
          )}

          {canExportEmail && (
            <Button
              variant="default"
              size="sm"
              className="shadow-xl"
              onClick={() => startAction('EXPORT_EMAIL', exportEmail)}
            >
              <MailPlus />

              {t('QUICK_ACTION_EXPORT_EMAIL')}
            </Button>
          )}

          {canExportPartyToMissingCrms && (
            <Button
              variant="default"
              size="sm"
              className="shadow-xl"
              onClick={() =>
                startAction('EXPORT_PARTY', exportPartyToMissingCrms)
              }
            >
              <UserRoundPlus />

              {t('QUICK_ACTION_EXPORT_PARTY')}
            </Button>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
